import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface UsePeriodicPopupProps {
  intervalMinutes?: number; // 기본값 10분
  enabled?: boolean; // 팝업 활성화 여부
}

export const usePeriodicPopup = ({ 
  intervalMinutes = 10, 
  enabled = true 
}: UsePeriodicPopupProps = {}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [lastPopupTime, setLastPopupTime] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();

  // 팝업이 비활성화되어야 하는 페이지들
  const disabledPages = ['/', '/guide01', '/register', '/contract', '/url-generator', '/analytics'];
  const isPopupDisabled = disabledPages.includes(location.pathname);

  useEffect(() => {
    if (!enabled || isPopupDisabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // 가입 시간 확인 (localStorage에서 가입 시간 가져오기)
    const registrationTime = localStorage.getItem('userRegistrationTime');
    const now = Date.now();

    if (!registrationTime) {
      // 가입 시간이 없으면 현재 시간을 가입 시간으로 설정
      localStorage.setItem('userRegistrationTime', now.toString());
      setLastPopupTime(now);
    } else {
      const regTime = parseInt(registrationTime);
      setLastPopupTime(regTime);
    }

    // 마지막 팝업 시간 확인
    const lastPopup = localStorage.getItem('lastPopupTime');
    if (lastPopup) {
      setLastPopupTime(parseInt(lastPopup));
    }

    // 10분마다 체크하는 인터벌 설정
    intervalRef.current = setInterval(() => {
      // 주기적 팝업 표시 횟수 확인 (최대 2개까지만 표시)
      const popupCount = parseInt(localStorage.getItem('periodicPopupCount') || '0');
      if (popupCount >= 2) {
        return;
      }

      // 메시지 전송 후 일시적으로 팝업 비활성화 (5분간)
      const messageSentTime = localStorage.getItem('messageSentTime');
      if (messageSentTime) {
        const timeSinceMessageSent = Date.now() - parseInt(messageSentTime);
        const cooldownPeriod = 5 * 60 * 1000; // 5분
        
        if (timeSinceMessageSent < cooldownPeriod) {
          return;
        }
      }

      const currentTime = Date.now();
      const lastPopupTimeValue = lastPopupTime || parseInt(localStorage.getItem('lastPopupTime') || '0');
      const registrationTimeValue = parseInt(localStorage.getItem('userRegistrationTime') || '0');
      
      // 마지막 팝업 시간이 있으면 그것을 기준으로, 없으면 가입 시간을 기준으로
      const baseTime = lastPopupTimeValue > registrationTimeValue ? lastPopupTimeValue : registrationTimeValue;
      const timeSinceLastPopup = currentTime - baseTime;

      // 10분(600,000ms)이 지났으면 팝업 표시
      if (timeSinceLastPopup >= intervalMinutes * 60 * 1000) {
        console.log({
          lastPopupTime: new Date(lastPopupTimeValue).toLocaleString(),
          timeSinceLastPopup: Math.round(timeSinceLastPopup / 1000 / 60) + '분',
          intervalMinutes,
          popupCount
        });
        setShowPopup(true);
        setLastPopupTime(currentTime);
        localStorage.setItem('lastPopupTime', currentTime.toString());
      }
    }, 60000); // 1분마다 체크

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, intervalMinutes, isPopupDisabled]);

  const handlePopupClose = () => {
    setShowPopup(false);
    // 팝업이 닫힐 때 현재 시간을 마지막 팝업 시간으로 설정
    const currentTime = Date.now();
    setLastPopupTime(currentTime);
    localStorage.setItem('lastPopupTime', currentTime.toString());
  };

  return {
    showPopup,
    handlePopupClose
  };
};
